import React, { useState } from "react";
import {
    Flex,
    Button,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Text,
    FormErrorMessage,
    useToast,
    SimpleGrid,
    GridItem,
    Select,
    Box,
    VStack,
    Icon,
    InputGroup,
    InputLeftElement,
    Badge,
    HStack,
    Image,
    useColorModeValue,
} from "@chakra-ui/react";
import { useLoading } from "Providers/LoadingProvider";
import { Link, useHistory } from "react-router-dom";
import { Field, Form, Formik } from "formik";
import AuthApi from "Api/AuthApi";
import InputMask from 'react-input-mask';
import { TbUser, TbMail, TbLock, TbPhone, TbMapPin, TbUserPlus, TbGift } from "react-icons/tb";

function SignUp() {
    const loading = useLoading();
    const history = useHistory();
    const toast = useToast({
        position: "top-right",
    });

    var initialValues = {
        name: "",
        email: "",
        password: "",
        password_confirmation: "",
        phone: "",
        city: "",
        state: "",
    };

    async function submitHandle(values, { setFieldError }) {
        loading.show();
        try {
            const response = await AuthApi.signUp(values);
            if (response.status !== 200) {
                throw new Error('Unexpected signup response');
            }

            // Analytics must never interrupt a successfully created account.
            try {
                if (typeof window.gtag === 'function') {
                    window.gtag('event', 'conversion', {
                        event_label: 'Criação de Conta'
                    });
                }
            } catch {
                // Continue to sign-in even when tracking is unavailable.
            }
            toast({ status: 'success', title: 'Cadastro realizado com sucesso! Faça login para continuar.', duration: 3000, variant: "solid", isClosable: true });
            history.push("/auth/signin");
        } catch (error) {
            if (error.response?.status === 422) {
                toast({ status: 'error', title: 'Erro, verifique seus dados!', duration: 3000, variant: "solid", isClosable: true });
                const errors = error.response.data?.errors || {};
                for (const field in errors) {
                    setFieldError(field, errors[field][0]);
                }
            } else {
                const status = error.response?.status;
                let reason = 'Ocorreu um erro inesperado ao processar o cadastro.';
                if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT' || status === 408 || status === 504) {
                    reason = 'O tempo de espera pela resposta do servidor foi excedido.';
                } else if (status === 429) {
                    reason = 'Muitas tentativas em pouco tempo. Aguarde alguns minutos antes de tentar novamente.';
                } else if (status === 401 || status === 403) {
                    reason = 'O servidor recusou a autorização para esta solicitação.';
                } else if (status >= 500) {
                    reason = 'O servidor encontrou uma falha ao processar o cadastro.';
                } else if (status) {
                    reason = 'O servidor recusou a solicitação de cadastro.';
                } else if (error.code === 'ERR_NETWORK' || error.request) {
                    reason = 'Não foi possível obter uma resposta do servidor. Verifique sua conexão com a internet.';
                }
                // The API can return raw database exceptions; show a useful category and status instead.
                const description = `${reason}${status ? ` (HTTP ${status})` : ''} Tente novamente. Se o problema persistir, entre em contato com o suporte.`;
                toast({ status: 'error', title: 'Não foi possível concluir o cadastro.', description, duration: 10000, variant: "solid", isClosable: true });
            }
        } finally {
            loading.hide();
        }
    }

    const [phoneMaskState, setPhoneMaskState] = useState('(99) 9999-99999');
    function checkPhoneNumberSize(event) {
        let size = event.target.value.split(' ').join('').split('-').join('');
        if (size.length <= 12) {
            setPhoneMaskState('(99) 9999-99999');
        } else {
            setPhoneMaskState('(99) 9 9999-9999');
        }
    }

    // Dark mode colors
    const cardBg = useColorModeValue("white", "gray.800");
    const inputBg = useColorModeValue("gray.50", "gray.700");
    const inputBorder = useColorModeValue("gray.200", "gray.600");
    const inputHoverBorder = useColorModeValue("gray.300", "gray.500");
    const inputFocusBg = useColorModeValue("white", "gray.700");
    const labelColor = useColorModeValue("gray.700", "gray.200");
    const textMuted = useColorModeValue("gray.500", "gray.400");
    const iconColor = useColorModeValue("gray.400", "gray.500");
    const linkColor = useColorModeValue("#2D6B3F", "green.300");

    const inputStyles = {
        borderRadius: "xl",
        fontSize: "sm",
        bg: inputBg,
        border: "2px solid",
        borderColor: inputBorder,
        _hover: { borderColor: inputHoverBorder },
        _focus: { borderColor: "#2D6B3F", bg: inputFocusBg, boxShadow: "0 0 0 1px #2D6B3F" },
    };

    return (
        <Flex w="100%" justify="center" px={4}>
            <Box
                w={{ base: "100%", md: "700px" }}
                bg={cardBg}
                borderRadius="2xl"
                boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.25)"
                overflow="hidden"
            >
                {/* Header */}
                <Box
                    bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                    py={8}
                    px={6}
                    textAlign="center"
                >
                    <Link to="/">
                        <Image
                            src="/wsclima-logo.png"
                            alt="WS Clima Logo"
                            h="60px"
                            w="auto"
                            mx="auto"
                            mb={3}
                            objectFit="contain"
                        />
                    </Link>
                    <Heading color="white" fontSize="2xl" fontWeight="bold">
                        Criar Conta
                    </Heading>
                    <HStack justify="center" mt={3} spacing={2}>
                        <Badge
                            bg="whiteAlpha.200"
                            color="white"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="sm"
                        >
                            A partir de R$ 29,99/mês
                        </Badge>
                        <Badge
                            bg="yellow.400"
                            color="gray.800"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="sm"
                            fontWeight="bold"
                        >
                            <HStack spacing={1}>
                                <Icon as={TbGift} />
                                <Text>30 dias grátis</Text>
                            </HStack>
                        </Badge>
                    </HStack>
                    <Text color="whiteAlpha.800" fontSize="sm" mt={3}>
                        Preencha seus dados e comece seu teste gratuito
                    </Text>
                </Box>

                {/* Form */}
                <Box p={8}>
                    <Formik onSubmit={submitHandle} initialValues={initialValues}>
                        <Form>
                            <SimpleGrid columns={12} gap={4}>
                                <GridItem colSpan={{ base: 12, lg: 8 }}>
                                    <Field name="name">
                                        {({ field, form }) => (
                                            <FormControl isRequired isInvalid={form.errors.name && form.touched.name}>
                                                <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                    Nome Completo
                                                </FormLabel>
                                                <InputGroup size="lg">
                                                    <InputLeftElement pointerEvents="none" h="100%">
                                                        <Icon as={TbUser} color={iconColor} />
                                                    </InputLeftElement>
                                                    <Input {...field} {...inputStyles} type="text" placeholder="João da Silva" />
                                                </InputGroup>
                                                <FormErrorMessage>{form.errors.name}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                </GridItem>

                                <GridItem colSpan={{ base: 12, lg: 4 }}>
                                    <Field name="phone">
                                        {({ field, form }) => (
                                            <FormControl isRequired isInvalid={form.errors.phone && form.touched.phone}>
                                                <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                    Telefone
                                                </FormLabel>
                                                <InputGroup size="lg">
                                                    <InputLeftElement pointerEvents="none" h="100%">
                                                        <Icon as={TbPhone} color={iconColor} />
                                                    </InputLeftElement>
                                                    <InputMask {...field} mask={phoneMaskState} maskChar="" onChange={(e) => { checkPhoneNumberSize(e); form.handleChange(e); }}>
                                                        {(inputProps) => <Input {...inputProps} {...inputStyles} type="tel" minLength="14" placeholder="(XX) 9 9999-9999" pl="2.5rem" />}
                                                    </InputMask>
                                                </InputGroup>
                                                <FormErrorMessage>{form.errors.phone}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                </GridItem>

                                <GridItem colSpan={{ base: 8, lg: 4 }}>
                                    <Field name="city">
                                        {({ field, form }) => (
                                            <FormControl isRequired isInvalid={form.errors.city && form.touched.city}>
                                                <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                    Cidade
                                                </FormLabel>
                                                <InputGroup size="lg">
                                                    <InputLeftElement pointerEvents="none" h="100%">
                                                        <Icon as={TbMapPin} color={iconColor} />
                                                    </InputLeftElement>
                                                    <Input {...field} {...inputStyles} type="text" placeholder="São Paulo" />
                                                </InputGroup>
                                                <FormErrorMessage>{form.errors.city}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                </GridItem>

                                <GridItem colSpan={{ base: 4, lg: 2 }}>
                                    <Field name="state">
                                        {({ field, form }) => (
                                            <FormControl isRequired isInvalid={form.errors.state && form.touched.state}>
                                                <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                    UF
                                                </FormLabel>
                                                <Select
                                                    {...field}
                                                    {...inputStyles}
                                                    size="lg"
                                                    placeholder="..."
                                                >
                                                    <option value="AC">AC</option>
                                                    <option value="AL">AL</option>
                                                    <option value="AP">AP</option>
                                                    <option value="AM">AM</option>
                                                    <option value="BA">BA</option>
                                                    <option value="CE">CE</option>
                                                    <option value="DF">DF</option>
                                                    <option value="ES">ES</option>
                                                    <option value="GO">GO</option>
                                                    <option value="MA">MA</option>
                                                    <option value="MT">MT</option>
                                                    <option value="MS">MS</option>
                                                    <option value="MG">MG</option>
                                                    <option value="PA">PA</option>
                                                    <option value="PB">PB</option>
                                                    <option value="PR">PR</option>
                                                    <option value="PE">PE</option>
                                                    <option value="PI">PI</option>
                                                    <option value="RJ">RJ</option>
                                                    <option value="RN">RN</option>
                                                    <option value="RS">RS</option>
                                                    <option value="RO">RO</option>
                                                    <option value="RR">RR</option>
                                                    <option value="SC">SC</option>
                                                    <option value="SP">SP</option>
                                                    <option value="SE">SE</option>
                                                    <option value="TO">TO</option>
                                                </Select>
                                                <FormErrorMessage>{form.errors.state}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                </GridItem>

                                <GridItem colSpan={{ base: 12, lg: 6 }}>
                                    <Field name="email">
                                        {({ field, form }) => (
                                            <FormControl isRequired isInvalid={form.errors.email && form.touched.email}>
                                                <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                    E-mail
                                                </FormLabel>
                                                <InputGroup size="lg">
                                                    <InputLeftElement pointerEvents="none" h="100%">
                                                        <Icon as={TbMail} color={iconColor} />
                                                    </InputLeftElement>
                                                    <Input {...field} {...inputStyles} type="email" placeholder="email@email.com" />
                                                </InputGroup>
                                                <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                </GridItem>

                                <GridItem colSpan={{ base: 12, lg: 6 }}>
                                    <Field name="password">
                                        {({ field, form }) => (
                                            <FormControl isRequired isInvalid={form.errors.password && form.touched.password}>
                                                <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                    Senha
                                                </FormLabel>
                                                <InputGroup size="lg">
                                                    <InputLeftElement pointerEvents="none" h="100%">
                                                        <Icon as={TbLock} color={iconColor} />
                                                    </InputLeftElement>
                                                    <Input {...field} {...inputStyles} type="password" placeholder="••••••••" />
                                                </InputGroup>
                                                <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                </GridItem>

                                <GridItem colSpan={{ base: 12, lg: 6 }}>
                                    <Field name="password_confirmation">
                                        {({ field, form }) => (
                                            <FormControl isRequired isInvalid={form.errors.password_confirmation && form.touched.password_confirmation}>
                                                <FormLabel fontSize="sm" fontWeight="medium" color={labelColor}>
                                                    Confirme a Senha
                                                </FormLabel>
                                                <InputGroup size="lg">
                                                    <InputLeftElement pointerEvents="none" h="100%">
                                                        <Icon as={TbLock} color={iconColor} />
                                                    </InputLeftElement>
                                                    <Input {...field} {...inputStyles} type="password" placeholder="••••••••" />
                                                </InputGroup>
                                                <FormErrorMessage>{form.errors.password_confirmation}</FormErrorMessage>
                                            </FormControl>
                                        )}
                                    </Field>
                                </GridItem>
                            </SimpleGrid>

                            <Button
                                type="submit"
                                bg="linear-gradient(135deg, #2D6B3F 0%, #1F5A2F 100%)"
                                color="white"
                                w="100%"
                                h={{ base: "auto", md: "50px" }}
                                minH="50px"
                                py={{ base: 3, md: 0 }}
                                whiteSpace="normal"
                                borderRadius="xl"
                                fontSize="md"
                                fontWeight="semibold"
                                mt={8}
                                _hover={{
                                    bg: "linear-gradient(135deg, #1F5A2F 0%, #164A24 100%)",
                                    transform: "translateY(-2px)",
                                    boxShadow: "lg",
                                }}
                                _active={{
                                    transform: "translateY(0)",
                                }}
                                transition="all 0.2s"
                                leftIcon={<TbUserPlus />}
                            >
                                <Text as="span" lineHeight="short">
                                    Criar conta e{" "}
                                    <Box as="span" display={{ base: "block", md: "inline" }}>
                                        Iniciar teste grátis
                                    </Box>
                                </Text>
                            </Button>
                        </Form>
                    </Formik>

                    {/* Link */}
                    <VStack spacing={3} mt={6}>
                        <Text color={textMuted} fontSize="sm" textAlign="center">
                            Já tem uma conta?{" "}
                            <Link to="/auth/signin">
                                <Text as="span" color={linkColor} fontWeight="bold" _hover={{ textDecoration: "underline" }}>
                                    Faça login
                                </Text>
                            </Link>
                        </Text>
                    </VStack>
                </Box>
            </Box>
        </Flex>
    );
}

export default SignUp;
